/**
 * Address formatting utilities for manipulation of address data.
 * @module util/addresses
 */

/**
 * Address object from the GraphQL Address type.
 * @typedef {object} Address
 * @property {string} [line1] - Primary street address line
 * @property {string} [line2] - Secondary address line (apartment, suite, unit, etc.)
 * @property {string} [city] - City or locality name
 * @property {string} [state] - State, province, or region
 * @property {string} [country] - Country name
 * @property {string} [postcode] - Postal or ZIP code
 * @see graphql/syndesi.graphql - Address type definition
 */

/**
 * Options for formatting an address string.
 * @typedef {object} AddressFormatOptions
 * @property {boolean} [includeCountry=false] - Whether to include the country in the
 *   formatted string. Defaults to false for UI display. Set to true for geocoding.
 */

/**
 * Formats an address as a complete string with all available components.
 * Useful for forms, property detail cards, and contexts requiring the full
 * postal address.
 *
 * The function combines line1, line2, and city with commas, then appends
 * state and postcode separated by spaces. Country is excluded by default
 * but can be included via the options parameter.
 * @param {Address|null|undefined} address - The address object to format
 * @param {object} [options={}] - Formatting options
 * @param {boolean} [options.includeCountry=false] - Whether to include the country field
 * @returns {string|null} Full formatted address string, or null if address is null/undefined
 * @example
 * // Default behavior - country excluded for UI display
 * getFullAddressString({
 *   line1: '79 BAY ST',
 *   line2: null,
 *   city: 'PATONGA',
 *   state: 'NSW',
 *   postcode: '2256',
 *   country: 'Australia',
 * });
 * // Returns: '79 BAY ST, PATONGA NSW 2256'
 * @example
 * // With country included - for geocoding/map queries
 * getFullAddressString({
 *   line1: '79 BAY ST',
 *   line2: null,
 *   city: 'PATONGA',
 *   state: 'NSW',
 *   postcode: '2256',
 *   country: 'Australia',
 * }, { includeCountry: true });
 * // Returns: '79 BAY ST, PATONGA NSW 2256 Australia'
 * @example
 * // Address with line2 - country excluded by default
 * getFullAddressString({
 *   line1: '123 Main St',
 *   line2: 'Unit 5',
 *   city: 'Melbourne',
 *   state: 'VIC',
 *   postcode: '3000',
 *   country: 'Australia',
 * });
 * // Returns: '123 Main St, Unit 5, Melbourne VIC 3000'
 * @example
 * // Null address returns null
 * getFullAddressString(null);
 * // Returns: null
 */
function getFullAddressString(address, { includeCountry = false } = {}) {
  if (!address || Object.keys(address).length === 0) {
    return null;
  }

  const {
    line1, line2, city, state, postcode, country,
  } = address;

  const parts = [
    [line1, line2, city].filter(Boolean).join(', '),
    state,
    postcode,
  ];

  if (includeCountry) {
    parts.push(country);
  }

  return parts.filter(Boolean).join(' ');
}

export default getFullAddressString;
